import { Add, AttachMoney, ReceiptLong, Remove } from "@mui/icons-material";
import { Card, Icon, Tooltip } from "@mui/material";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { ConfirmarContext } from "context/ConfirmarContext";
import { memo, useContext, useMemo } from "react";
import { formatMoneyPunto } from "utils/formatters";
import { useModalPedido } from "../context/ModalPedidoContext";

const quantityBoxSx = {
  minWidth: 42,
  height: 38,
  px: 1,
  borderRadius: 2,
  border: "1px solid rgba(15, 23, 42, 0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#fff",
};

const iconActionSx = {
  minWidth: 0,
  width: 40,
  height: 40,
  borderRadius: "14px",
  p: 0,
  backgroundColor: "#fff",
};

const getDetalleIdentity = (detalle = {}) =>
  detalle?.optimisticKey
    ? detalle.optimisticKey
    : detalle?.id
    ? `id:${detalle.id}`
    : `prod:${detalle?.producto?.id || detalle?.idProducto || "na"}:comanda:${
        detalle?.numeroComanda || "na"
      }`;

const getStepperButtonSx = (isDisabled, palette) => ({
  width: 38,
  minWidth: 38,
  height: 38,
  borderRadius: "50%",
  border: "none",
  outline: "none",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  appearance: "none",
  backgroundColor: palette.background,
  color: "#fff",
  boxShadow: "none",
  opacity: isDisabled ? 0.5 : 1,
  cursor: isDisabled ? "not-allowed" : "pointer",
  transition: "none",
  WebkitTapHighlightColor: "transparent",
  "&:hover": {
    backgroundColor: palette.background,
  },
  "&:focus": {
    outline: "none",
    boxShadow: "none",
  },
  "&:focus-visible": {
    outline: "none",
    boxShadow: "none",
  },
  "&:active": {
    backgroundColor: palette.background,
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: palette.background,
    color: "#fff",
    opacity: 0.5,
    cursor: "not-allowed",
  },
  "& svg": {
    color: "#fff",
  },
  "&:disabled svg": {
    color: "#fff",
  },
});

const ListadoDetalles = memo(() => {
  const { confirmar } = useContext(ConfirmarContext);
  const { pedido, total, disabled, pendingDetalleIds, ejecutarAccion, onCantidadChange } =
    useModalPedido();

  const detallesVisibles = useMemo(
    () => (pedido?.detalles || []).filter((detalle) => detalle?.estado !== "CANCELADO"),
    [pedido?.detalles]
  );

  if (detallesVisibles.length < 1) {
    return <SinDatos mensaje="No hay productos agregados." />;
  }

  return (
    <SoftBox sx={{ position: "relative", pb: 7 }}>
      <Card
        sx={{
          position: "relative",
          zIndex: 1,
          borderRadius: 4,
          border: "1px solid rgba(15, 23, 42, 0.12)",
          boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
          overflow: "visible",
          backgroundColor: "#fff8fb",
        }}
      >
        <SoftBox p={1.5} display="flex" flexDirection="column" gap={1.25}>
          {detallesVisibles.map((detalle) => {
            const estaEntregado = detalle?.estado === "ENTREGADO";
            const detallePending = !!pendingDetalleIds?.[getDetalleIdentity(detalle)];

            return (
              <Card
                key={`${detalle?.id || "nuevo"}-${detalle?.producto?.id}-${
                  detalle?.numeroComanda || "sin-comanda"
                }`}
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.08)",
                  overflow: "hidden",
                }}
              >
                <SoftBox
                  p={1.5}
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  gap={1.5}
                  flexWrap="wrap"
                >
                  <SoftBox flex={1} minWidth={0}>
                    <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <SoftTypography
                        variant="button"
                        fontWeight="bold"
                        color="dark"
                        sx={{
                          fontSize: "0.95rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {detalle?.producto?.nombre}
                      </SoftTypography>
                      <SoftBadge
                        color={estaEntregado ? "success" : "warning"}
                        badgeContent={`x${detalle?.cantidad}`}
                        container
                      />
                    </SoftBox>

                    <SoftBox mt={0.75} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                      <SoftBox display="flex" alignItems="center" gap={0.5}>
                        <ReceiptLong sx={{ fontSize: 18, color: "#344767" }} />
                        <SoftTypography variant="caption" color="text" fontWeight="medium">
                          {detalle?.numeroComanda
                            ? `Comanda #${detalle.numeroComanda}`
                            : "Sin comanda"}
                        </SoftTypography>
                      </SoftBox>

                      {detalle?.precio === 0 ? (
                        <SoftBadge color="error" badgeContent="SIN PRECIO" container />
                      ) : (
                        <SoftBox display="flex" alignItems="center" gap={0.25}>
                          <AttachMoney sx={{ fontSize: 18, color: "#344767" }} />
                          <SoftTypography variant="caption" color="text" fontWeight="medium">
                            Precio: ${formatMoneyPunto(detalle?.precio)}
                          </SoftTypography>
                        </SoftBox>
                      )}
                    </SoftBox>
                  </SoftBox>

                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={0.5}
                    flexWrap="wrap"
                    flexShrink={0}
                    width={{ xs: "100%", sm: "auto" }}
                  >
                    {estaEntregado ? (
                      <SoftBadge color="success" badgeContent="ENTREGADO" container />
                    ) : (
                      <>
                        <SoftBox
                          component="button"
                          type="button"
                          onClick={() => onCantidadChange(detalle, -1)}
                          disabled={disabled || detallePending}
                          sx={getStepperButtonSx(disabled || detallePending, {
                            background: "#b7c1d0",
                          })}
                        >
                          <Remove fontSize="small" />
                        </SoftBox>
                        <SoftBox sx={quantityBoxSx}>
                          <SoftTypography variant="button" fontWeight="bold" color="dark">
                            {detalle?.cantidad}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="button"
                          type="button"
                          onClick={() => onCantidadChange(detalle, 1)}
                          disabled={disabled || detallePending || detalle?.precio === 0}
                          sx={getStepperButtonSx(
                            disabled || detallePending || detalle?.precio === 0,
                            {
                              background: "#ffcc39",
                            }
                          )}
                        >
                          <Add fontSize="small" />
                        </SoftBox>
                        <Tooltip title="Marcar entregado" placement="top">
                          <span>
                            <SoftButton
                              iconOnly
                              size="small"
                              circular
                              onClick={() => ejecutarAccion("listo", detalle)}
                              disabled={disabled || detallePending || detalle?.precio === 0}
                              sx={{
                                ...iconActionSx,
                                backgroundColor: "#64c7c4",
                                color: "#fff",
                                boxShadow: "0 8px 18px rgba(100, 199, 196, 0.2)",
                                "&:hover": {
                                  backgroundColor: "#54bab7",
                                },
                                "&.Mui-disabled": {
                                  color: "rgba(255, 255, 255, 0.72)",
                                },
                              }}
                            >
                              <Icon sx={{ fontSize: 18, fontWeight: "bold", color: "inherit" }}>
                                done
                              </Icon>
                            </SoftButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Quitar producto" placement="top">
                          <span>
                            <SoftButton
                              iconOnly
                              size="small"
                              circular
                              onClick={() => {
                                confirmar({
                                  icon: "error",
                                  title: "Quitar producto",
                                  message:
                                    "El producto sera quitado del pedido. Si corresponde, indique el motivo.",
                                  func: async (motivo) => {
                                    ejecutarAccion("borrar", detalle, motivo);
                                  },
                                });
                              }}
                              disabled={disabled || detallePending}
                              sx={{
                                ...iconActionSx,
                                backgroundColor: "#ec008c",
                                color: "#fff",
                                boxShadow: "0 8px 18px rgba(236, 0, 140, 0.2)",
                                "&:hover": {
                                  backgroundColor: "#d90080",
                                },
                                "&.Mui-disabled": {
                                  color: "rgba(255, 255, 255, 0.72)",
                                },
                              }}
                            >
                              <Icon sx={{ fontSize: 18, color: "inherit" }}>delete</Icon>
                            </SoftButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </SoftBox>
                </SoftBox>
              </Card>
            );
          })}
        </SoftBox>
      </Card>

      <Card
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 10,
          zIndex: 0,
          borderRadius: 3,
          border: "1px solid rgba(236, 0, 140, 0.18)",
          background: "linear-gradient(90deg, #ec008c 0%, #ff2b93 100%)",
          boxShadow: "0 14px 28px rgba(236, 0, 140, 0.24)",
        }}
      >
        <SoftBox pt={4} pb={2} display="flex" alignItems="center" justifyContent="center">
          <SoftTypography variant="button" fontWeight="bold" color="white">
            Total: ${formatMoneyPunto(total || 0)}
          </SoftTypography>
        </SoftBox>
      </Card>
    </SoftBox>
  );
});

export default ListadoDetalles;
